import { useState, useRef, useEffect } from 'react'
import { X, ChevronDown } from 'lucide-react'

export interface Option {
  id: string | number
  label: string
  value: string | number
}

export interface MultipleSelectorProps {
  options: Option[]
  selected: (string | number)[]
  onChange: (selected: (string | number)[]) => void
  placeholder?: string
  maxItems?: number
  searchable?: boolean
  clearable?: boolean
  disabled?: boolean
}

export function MultipleSelector({
  options,
  selected,
  onChange,
  placeholder = 'Select items...',
  maxItems,
  searchable = true,
  clearable = true,
  disabled = false,
}: MultipleSelectorProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const containerRef = useRef<HTMLDivElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchable && searchInputRef.current) {
      searchInputRef.current.focus()
    }
  }, [isOpen, searchable])

  const selectedItems = options.filter((opt) => selected.includes(opt.id))
  const filteredOptions = searchable
    ? options.filter(
        (opt) =>
          !selected.includes(opt.id) &&
          opt.label.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : options.filter((opt) => !selected.includes(opt.id))

  const handleSelect = (id: string | number) => {
    if (maxItems && selected.length >= maxItems) {
      return
    }
    onChange([...selected, id])
    setSearchQuery('')
  }

  const handleRemove = (id: string | number) => {
    onChange(selected.filter((item) => item !== id))
  }

  const handleClear = () => {
    onChange([])
    setSearchQuery('')
    setIsOpen(false)
  }

  const isMaxReached = !!(maxItems && selected.length >= maxItems)

  return (
    <div ref={containerRef} className="relative w-full ">
      {/* Main input area */}
<button
  type="button"   // ✅ ADD THIS
  onClick={() => !disabled && setIsOpen(!isOpen)}
  disabled={disabled}
  className={`w-full rounded-lg border border-input bg-background px-3 py-2 text-sm transition-colors ${
    isOpen ? "ring-2 ring-ring" : ""
  } ${
    disabled
      ? "cursor-not-allowed opacity-50"
      : "cursor-pointer hover:border-muted-foreground"
  }`}
>
        <div className="flex items-center justify-between">
          <div className="flex flex-wrap gap-1 ">
            {selected.length === 0 ? (
              <span className="text-muted-foreground bg-white">{placeholder}</span>
            ) : (
              selectedItems.map((item) => (
                <div
                  key={item.id}
                  className="flex items-center gap-1 rounded-md bg-primary px-2 py-1 text-xs text-primary-foreground"
                  onClick={(e) => e.stopPropagation()}
                >
                  <span className='text-white'>{item.label}</span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      handleRemove(item.id)
                    }}
                    className="hover:opacity-80 text-white rounded bg-transparent p-1"
                    type="button"
                  >
                    <X size={14} />
                  </button>
                </div>
              ))
            )}
          </div>
          <div className="flex items-center gap-1">
            {clearable && selected.length > 0 && (
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  handleClear()
                }}
                className="p-1 hover:bg-muted rounded bg-white"
                type="button"
              >
                <X size={16} className="text-muted-foreground" />
              </button>
            )}
            <ChevronDown
              size={16}
              className={`text-muted-foreground transition-transform ${
                isOpen ? 'rotate-180' : ''
              }`}
            />
          </div>
        </div>
      </button>

      {/* Dropdown menu */}
      {isOpen && (
        <div className="absolute top-full left-0 right-0 z-50 mt-2 rounded-lg border border-input bg-white shadow-lg">
          {/* Search input */}
          {searchable && (
            <div className="border-b border-input p-2">
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full rounded px-2 py-1 text-sm bg-muted focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
          )}

          {/* Options list */}
          <div className="max-h-48 overflow-y-auto p-2">
            {filteredOptions.length === 0 ? (
              <div className="px-2 py-3 text-center text-sm text-muted-foreground">
                {searchQuery ? 'No results found' : 'No options available'}
              </div>
            ) : (
              filteredOptions.map((option) => (
                <button
                  key={option.id}
                  onClick={() => handleSelect(option.id)}
                  disabled={isMaxReached}
                  className={`w-full rounded px-2 py-2 text-left text-sm bg-white ${
                    isMaxReached
                      ? 'cursor-not-allowed opacity-50'
                      : 'hover:bg-muted cursor-pointer'
                  }`}
                  type="button"
                >
                  {option.label}
                </button>
              ))
            )}
          </div>

          {/* Footer info */}
          {maxItems && (
            <div className="border-t border-input px-2 py-1 text-xs text-muted-foreground">
              {selected.length} / {maxItems} selected
            </div>
          )}
        </div>
      )}
    </div>
  )
}
